import React, { useState, useEffect, createRef } from "react";
import { Link } from "react-router-dom";
import { Table, Row, Col, Input, Space, Button, InputRef, Modal } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import NumberFormat from "react-number-format";
import { formatUTCDate } from "../../date/DateOp";
import { deleteOrderForm, listOrders, updateOrderForm } from "../../../common/api/OrderFormClient";
import {
    Filter,
    FilterGroupType,
    FilterOperand,
    Pagination,
    Sort,
    sortOrderTranslate,
} from "../../../types/DataFetchingTypes";
import { getVendors } from "../../../common/api/VendorClient";
import { Vendor } from "../../../types/VendorTypes";
import { getUsers, listUsers } from "../../../common/api/UserClient";
import { User } from "../../../types/UserGroup";
import { OwnerCell } from "../../../common/components/OwnerCell";
import { ContractType, OrderForm } from "../../../types/OrderFormTypes";
import { SortFilter } from "../../../common/CommonInterfaces";
import { handleError } from "../../../common/ErrorHandling";
import { SimulationScore } from "../../dashboard-v2/components/SimulationScore";
import { NotificationModal } from "./NotificationModal";
import { roundToTwoDecimals } from "../../../common/Number";
import { SvgIcon } from "../../../common/components/SvgIcon";
import { DateTime } from "../../../common/components/DateTime";

const { Column } = Table;

interface OrderFormListProps {
    expired?: boolean;
    showNotify?: boolean;
    showDelete?: boolean;
    showScore?: boolean;
    vendorKeys?: string[];
    refresh?: number;
    onDelete?: () => void;
    includeZeroScore?: boolean;
}

export const OrderFormList: React.FC<OrderFormListProps> = ({
    expired,
    showNotify,
    showDelete,
    showScore = true,
    vendorKeys = [],
    refresh,
    onDelete,
    includeZeroScore = false,
}) => {
    const [data, setData]: any[] = useState([]);
    const [pagination, setPagination] = useState({
        pages: 0,
        current: 1,
        total: 0,
        pageSize: 10,
    } as Pagination);

    const [sortFilter, setSortFilter] = useState({ sort: [], filter: [] } as SortFilter);
    const [loading, setLoading]: any[] = useState(true);
    const [vendors, setVendors] = useState({} as Map<string, Vendor>);
    const [users, setUsers] = useState({} as Map<number, User>);
    const [reload, setReload]: any[] = useState(0);
    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [orderFormId, setOrderFormId] = useState(-1);
    const [filterUsers, setFilterUsers] = useState<User[]>([]);

    const searchRef = createRef<InputRef>();

    const handleTableChange = async (pagination: any, filters_: any, sorter: any) => {
        setPagination(pagination);

        const fs = { filter: [], sort: [] } as SortFilter;

        if (sorter.columnKey && sorter.order) {
            fs.sort = [
                { attribute: sorter.columnKey, order: sortOrderTranslate(sorter.order) } as Sort,
            ];
        }
        if (filters_.contractType) {
            const types = filters_.contractType.map((v: string) => {
                return {
                    attribute: "contractType",
                    operand: FilterOperand.EQUALS,
                    value: v,
                } as Filter;
            });

            const groups = {
                groupId: "contractType",
                groupType: FilterGroupType.OR,
                filter: types,
            };

            fs.filter.push(groups);
        }
        if (filters_.ownerId) {
            const owners = filters_.ownerId.map((v: string) => {
                return {
                    attribute: "ownerId",
                    operand: FilterOperand.EQUALS,
                    value: v,
                } as Filter;
            });

            const groups = {
                groupId: "ownerId",
                groupType: FilterGroupType.OR,
                filter: owners,
            };

            fs.filter.push(groups);
        }
        if (filters_.name) {
            const names = filters_.name.map((v: string) => {
                return {
                    attribute: "name",
                    operand: FilterOperand.SIMILAR,
                    value: v,
                } as Filter;
            });
            const nameSearch = {
                groupId: "name",
                groupType: FilterGroupType.OR,
                filter: names,
            };
            fs.filter.push(nameSearch);
        }

        setSortFilter(fs);
        setReload(reload + 1);
    };

    const handleNameSearch = async (_selectedKeys: any, confirm: any) => {
        confirm({ closeDropdown: true });
    };

    const handleReset = (clearFilter: any) => {
        clearFilter();
    };

    const handleDelete = (orderFormId: number) => {
        Modal.confirm({
            title: "Remove Order Form?",
            content: "Are you sure you want to remove the order form?",
            onOk: async () => {
                await deleteOrderForm(orderFormId);
                onDelete?.();
            },
            okText: "Remove",
        });
    };

    const configureNotification = (orderFormId: number) => {
        setOrderFormId(orderFormId);
        setShowNotificationModal(true);
    };

    const loadFilterUsers = async () => {
        try {
            const users = await listUsers();
            setFilterUsers(users);
        } catch (error) {
            handleError(error);
        }
    };

    useEffect(() => {
        loadFilterUsers();
    }, []);

    useEffect(() => {
        setReload((pre: number) => pre + 1);
    }, [refresh]);

    useEffect(() => {
        setLoading(true);
        listOrders(
            pagination,
            sortFilter.sort,
            sortFilter.filter,
            expired,
            vendorKeys,
            undefined,
            undefined,
            undefined,
            undefined,
            includeZeroScore
        )
            .then(async (pager) => {
                if (pager.data.length > 0) {
                    const ownerIds = pager.data
                        .map((v) => v.ownerId)
                        .filter((id) => id !== undefined && id !== -1) as number[];
                    const uniqueOwnerIds = Array.from(new Set(ownerIds));

                    const [vendors, fetchedUsers] = await Promise.all([
                        getVendors(pager.data.map((v) => v.vendorKey)),
                        uniqueOwnerIds.length > 0 ? getUsers(uniqueOwnerIds) : Promise.resolve([]),
                    ]);
                    const vendorMap = new Map(vendors.map((v) => [v.vendorKey, v]));
                    const userMap = new Map(fetchedUsers.map((u) => [u.id, u]));
                    setVendors(vendorMap);
                    setUsers(userMap);
                }

                setData(pager.data);
                setPagination(pager.pagination);
            })
            .catch((error) => {
                handleError(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [reload]);

    const showAction = showNotify || showDelete;

    return (
        <div className="contract-panel" style={{ paddingTop: 10 }}>
            <Row key="contentTable">
                <Col span={24}>
                    <Table
                        dataSource={data}
                        pagination={pagination}
                        loading={loading}
                        rowKey="id"
                        size="small"
                        onChange={handleTableChange}
                    >
                        <Column
                            title="Name"
                            dataIndex="name"
                            key="name"
                            sorter={true}
                            render={(value, row: OrderForm) => {
                                return <Link to={"/order/" + row.id + ""}>{value}</Link>;
                            }}
                            filterIcon={<SearchOutlined />}
                            onFilter={(_value, _record) => {
                                return true;
                            }}
                            filterDropdown={({
                                setSelectedKeys,
                                selectedKeys,
                                confirm,
                                clearFilters,
                            }) => {
                                return (
                                    <div style={{ padding: 8 }}>
                                        <Input
                                            ref={searchRef}
                                            placeholder={`Search `}
                                            value={selectedKeys[0]}
                                            onChange={(e) =>
                                                setSelectedKeys(
                                                    e.target.value ? [e.target.value] : []
                                                )
                                            }
                                            onPressEnter={() => {
                                                handleNameSearch(selectedKeys, confirm);
                                            }}
                                            style={{ marginBottom: 8, display: "block" }}
                                        />
                                        <Space>
                                            <Button
                                                type="primary"
                                                onClick={() =>
                                                    handleNameSearch(selectedKeys, confirm)
                                                }
                                                icon={<SearchOutlined />}
                                                size="small"
                                                style={{ width: 90 }}
                                            >
                                                Search
                                            </Button>
                                            <Button
                                                onClick={() => handleReset(clearFilters)}
                                                size="small"
                                                style={{ width: 90 }}
                                            >
                                                Reset
                                            </Button>
                                            <Button
                                                type="link"
                                                size="small"
                                                onClick={() => {
                                                    confirm({ closeDropdown: true });
                                                }}
                                            >
                                                Close
                                            </Button>
                                        </Space>
                                    </div>
                                );
                            }}
                            onFilterDropdownVisibleChange={(visible) => {
                                if (visible) {
                                    setTimeout(() => searchRef.current?.focus(), 100);
                                }
                            }}
                        />

                        <Column
                            title="Vendor"
                            dataIndex="vendorKey"
                            key="vendorKey"
                            sorter={true}
                            render={(value) => {
                                const vendor = vendors.get(value);
                                return (
                                    <Link to={`/vendor/${vendor?.vendorKey}`}>{vendor?.name}</Link>
                                );
                            }}
                        />

                        <Column
                            title="Upload date"
                            dataIndex="recordCreationDate"
                            key="recordCreationDate"
                            sorter={true}
                            render={(value) => <DateTime date={value} />}
                        />

                        <Column
                            title="Owner"
                            dataIndex="ownerId"
                            key="ownerId"
                            filters={filterUsers.map((u) => ({
                                text: u.name,
                                value: u.id,
                            }))}
                            render={(value, row: OrderForm) => {
                                const user = users.get(value);
                                return (
                                    <OwnerCell
                                        ownerId={value}
                                        ownerName={user?.name}
                                        onSave={async (newOwnerId) => {
                                            await updateOrderForm({
                                                id: row.id,
                                                ownerId: newOwnerId,
                                            } as OrderForm);
                                        }}
                                        onUpdate={() => setReload((prev: number) => prev + 1)}
                                    />
                                );
                            }}
                        />

                        <Column
                            title="Renewal date"
                            dataIndex="orderEndDate"
                            key="orderEndDate"
                            sorter={true}
                            render={(value) => {
                                return formatUTCDate(value);
                            }}
                        />
                        <Column
                            title="Value"
                            dataIndex="value"
                            key="centsValue"
                            sorter={true}
                            render={(value) => {
                                return (
                                    <NumberFormat
                                        value={value}
                                        thousandSeparator={true}
                                        displayType={"text"}
                                        prefix={"$"}
                                    />
                                );
                            }}
                        />
                        {showScore && (
                            <Column
                                title="Contract Score"
                                dataIndex="orderScore"
                                key="orderScore"
                                sorter={true}
                                render={(value, row: OrderForm) => {
                                    return (
                                        <Space>
                                            <Link to={`/order/score/${row.id}`}>
                                                <SvgIcon name="document" size={12} />
                                            </Link>
                                            <SimulationScore score={roundToTwoDecimals(value)} />
                                        </Space>
                                    );
                                }}
                            />
                        )}

                        <Column
                            title="Contract Type"
                            dataIndex="contractType"
                            key="contractType"
                            filters={Object.values(ContractType).map((v) => {
                                return {
                                    text: v,
                                    value: v,
                                };
                            })}
                        />

                        {showAction && (
                            <Column
                                dataIndex="id"
                                key="notify"
                                render={(value: number) => {
                                    return (
                                        <>
                                            {showNotify && (
                                                <Button
                                                    type="link"
                                                    icon={
                                                        <SvgIcon name="notifications" size={12} />
                                                    }
                                                    onClick={() => configureNotification(value)}
                                                ></Button>
                                            )}
                                            {showDelete && (
                                                <Button
                                                    type="link"
                                                    icon={<SvgIcon name="trash" size={12} />}
                                                    onClick={() => handleDelete(value)}
                                                ></Button>
                                            )}
                                        </>
                                    );
                                }}
                            ></Column>
                        )}
                    </Table>
                </Col>
            </Row>
            <NotificationModal
                open={showNotificationModal}
                resourceId={orderFormId}
                onOk={() => setShowNotificationModal(false)}
                onCancel={() => setShowNotificationModal(false)}
            />
        </div>
    );
};
